import { useEffect, useState, type ReactNode } from 'react'
import type { ResumoDaEntrada, UsuarioSessao } from '@crm/shared'
import { useResumoDaEntrada } from '../api/hooks-entrada'
import { Marca } from '../componentes/Marca'
import { juntar } from '../componentes/ui'

/**
 * A tela que cobre a carga do sistema depois de entrar.
 *
 * O pedido foi "a plataforma não carregar na frente do usuário": sem ela, o
 * bate-papo aparecia em pedaços — lista vazia, depois conversas, depois o
 * cronômetro de cada uma, depois a foto dos contatos. Aqui o sistema monta
 * atrás desta tela e só aparece pronto.
 *
 * Os cartões não são enfeite de carregamento: são os números do dia daquela
 * pessoa, buscados enquanto ela espera. Assim os segundos que o sistema levaria
 * de qualquer jeito passam dizendo alguma coisa — quantas conversas estão
 * paradas com ela, o que está na fila, o que sai hoje.
 *
 * A logo diz "FaroEscolar System" (ADR 0072): a tela abre o sistema inteiro, e
 * a pessoa sai dela para o CRM, o Marketing ou a Administração.
 */

/** O tempo do desaparecimento, casado com a transição de opacidade do CSS. */
const SAIDA_MS = 260

/*
  Uma cor da marca por cartão, para o olho separar os quatro sem ler o título.
  As classes ficam escritas por inteiro porque o Tailwind só gera o que acha no
  código. O âmbar escurece no claro (`amber-700`), como no selo da
  Administração: o âmbar da marca some sobre o branco.
*/
const TONS = {
  marca: { quadro: 'bg-marca/10 text-marca', numero: 'text-marca' },
  turquesa: { quadro: 'bg-faro-turquesa/10 text-faro-turquesa', numero: 'text-faro-turquesa' },
  verde: { quadro: 'bg-faro-verde/10 text-faro-verde', numero: 'text-faro-verde' },
  ambar: {
    quadro: 'bg-faro-ambar/15 text-amber-700 dark:text-faro-ambar',
    numero: 'text-amber-700 dark:text-faro-ambar',
  },
} as const

interface Cartao {
  chave: keyof ResumoDaEntrada
  titulo: string
  unidade: (quantidade: number) => string
  frase: (quantidade: number) => string
  tom: keyof typeof TONS
  desenho: ReactNode
}

const CARTOES: Cartao[] = [
  {
    chave: 'naoRespondidas',
    titulo: 'Esperando você',
    unidade: (n) => (n === 1 ? 'conversa' : 'conversas'),
    frase: (n) =>
      n === 0
        ? 'Nenhuma conversa sua está sem resposta. Começou o dia limpo.'
        : n === 1
          ? 'Uma conversa sua continua sem resposta desde o último atendimento.'
          : `${n} conversas suas continuam sem resposta e sobem no topo da lista.`,
    tom: 'marca',
    desenho: <Bolhas />,
  },
  {
    chave: 'naFila',
    titulo: 'Na fila',
    unidade: () => 'sem dono',
    frase: (n) =>
      n === 0
        ? 'A fila está vazia: toda conversa aberta já tem responsável.'
        : n === 1
          ? 'Uma conversa aberta ainda não tem responsável.'
          : `${n} conversas abertas ainda não têm responsável.`,
    tom: 'turquesa',
    desenho: <Fila />,
  },
  {
    chave: 'programadasHoje',
    titulo: 'Programadas para hoje',
    unidade: (n) => (n === 1 ? 'mensagem' : 'mensagens'),
    frase: (n) =>
      n === 0
        ? 'Nenhuma mensagem programada sai hoje.'
        : n === 1
          ? 'Uma mensagem programada sai hoje, no horário marcado.'
          : `${n} mensagens programadas saem hoje, nos horários marcados.`,
    tom: 'verde',
    desenho: <Relogio />,
  },
  {
    chave: 'tarefasDeHoje',
    titulo: 'Tarefas de hoje',
    unidade: () => 'no calendário',
    frase: (n) =>
      n === 0
        ? 'Nada marcado para hoje no seu calendário.'
        : n === 1
          ? 'Uma tarefa marcada para hoje, ainda aberta.'
          : `${n} tarefas marcadas para hoje, ainda abertas.`,
    tom: 'ambar',
    desenho: <Lista />,
  },
]

export function TelaDeEntrada({
  usuario,
  aoFechar,
}: {
  usuario: UsuarioSessao
  aoFechar: () => void
}) {
  const { data: resumo } = useResumoDaEntrada()
  const [saindo, setSaindo] = useState(false)

  /*
    Sem relógio e sem barra (18/10/2026): a tela fica até a pessoa clicar em
    "Entrar agora". O sistema continua montando atrás enquanto ela lê.
  */
  useEffect(() => {
    if (!saindo) return
    const fim = setTimeout(aoFechar, SAIDA_MS)
    return () => clearTimeout(fim)
  }, [saindo, aoFechar])

  const visiveis = CARTOES.filter((c) => resumo?.[c.chave] != null).slice(0, 4)

  return (
    <div
      /*
        `fixed inset-0` e não um wrapper do conteúdo: o sistema já está montado
        embaixo, carregando. Esta tela só tampa a montagem.
      */
      className={juntar(
        'fixed inset-0 z-50 overflow-y-auto bg-tinta-50 transition-opacity duration-200',
        saindo ? 'pointer-events-none opacity-0' : 'opacity-100',
      )}
      aria-live="polite"
      aria-label="O seu dia"
    >
      {/*
        O fundo: a faixa da marca no alto, como na barra do topo e no cartão do
        login, e duas manchas de luz nas cores dela. Tudo pelos tokens, então o
        escuro troca sozinho; e tudo `fixed`, para não rolar junto com os cartões
        em tela baixa.
      */}
      <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-linear-to-r from-marca via-faro-turquesa to-faro-verde" />
        <div className="absolute -top-48 left-1/2 h-[30rem] w-[30rem] -translate-x-[85%] rounded-full bg-marca/10 blur-3xl" />
        <div className="absolute -bottom-56 left-1/2 h-[32rem] w-[32rem] translate-x-[5%] rounded-full bg-faro-verde/10 blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-full max-w-4xl flex-col items-center justify-center gap-10 px-4 py-12">
        <header className="flex flex-col items-center text-center">
          <h1 className="flex justify-center">
            <Marca tamanho="g" produto="System" />
          </h1>
          <p className="mt-8 rounded-full bg-superficie px-3.5 py-1 text-xs font-semibold text-tinta-500 shadow-suave ring-1 ring-tinta-200/60">
            {hoje()}
          </p>
          <p className="mt-4 font-titulo text-3xl font-bold tracking-tight text-tinta-900 sm:text-4xl">
            {saudacao()}, {primeiroNome(usuario.nome)}
          </p>
          <p className="mt-2 text-base text-tinta-500">O seu dia, antes de começar.</p>
        </header>

        {visiveis.length > 0 ? (
          <div className="grid w-full gap-4 sm:grid-cols-2">
            {visiveis.map((cartao, indice) => (
              <CartaoDaEntrada
                key={cartao.chave}
                cartao={cartao}
                quantidade={resumo?.[cartao.chave] ?? 0}
                atraso={indice * 90}
              />
            ))}
          </div>
        ) : (
          /*
            Sem cartão nenhum a tela não pode ficar vazia: acontece com usuário
            recém-criado, antes de receberem permissão para alguma tela.
          */
          <p className="text-sm text-tinta-400">Abrindo o sistema…</p>
        )}

        <button
          type="button"
          onClick={() => setSaindo(true)}
          className="group inline-flex items-center gap-2 rounded-full bg-marca px-8 py-3 text-base font-semibold text-white shadow-alta shadow-marca/30 transition hover:-translate-y-px hover:bg-marca-escura focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-marca"
        >
          Entrar agora
          <span aria-hidden className="transition group-hover:translate-x-0.5">
            →
          </span>
        </button>
      </div>
    </div>
  )
}

function CartaoDaEntrada({
  cartao,
  quantidade,
  atraso,
}: {
  cartao: Cartao
  quantidade: number
  atraso: number
}) {
  const tom = TONS[cartao.tom]
  return (
    <article
      className="cartao-da-entrada flex flex-col rounded-2xl bg-superficie p-5 shadow-suave ring-1 ring-tinta-200/60"
      style={{ animationDelay: `${atraso}ms` }}
    >
      <div className="flex items-start justify-between gap-4">
        {/*
          O desenho num quadro da cor do cartão, ao lado do número, e não mais
          embaixo dele: antes o número ficava por cima e tampava o desenho.
        */}
        <div className={juntar('h-16 w-16 shrink-0 overflow-hidden rounded-xl', tom.quadro)}>
          {cartao.desenho}
        </div>
        {/* O número é o que se lê de longe; com alguma coisa esperando, ganha a cor do cartão. */}
        <p className="text-right">
          <span
            className={juntar(
              'block font-titulo text-4xl font-bold leading-none tabular-nums',
              quantidade > 0 ? tom.numero : 'text-tinta-900',
            )}
          >
            {quantidade.toLocaleString('pt-BR')}
          </span>
          <span className="mt-1.5 block text-xs font-medium text-tinta-400">
            {cartao.unidade(quantidade)}
          </span>
        </p>
      </div>
      <h2 className="mt-4 text-base font-semibold text-tinta-900">{cartao.titulo}</h2>
      <p className="mt-1 text-sm leading-snug text-tinta-500">{cartao.frase(quantidade)}</p>
    </article>
  )
}

function saudacao(): string {
  const hora = new Date().getHours()
  if (hora < 12) return 'Bom dia'
  if (hora < 18) return 'Boa tarde'
  return 'Boa noite'
}

/** "Sábado, 18 de outubro": o dia, sem a hora, que relógio a tela não tem. */
function hoje(): string {
  const texto = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome
}

/*
  Os desenhos.

  São de enfeite e por isso ficam escondidos do leitor de tela: o que eles
  ilustram já está escrito no número, no título e na frase do cartão. Todos
  herdam a cor do texto (`currentColor`), que vem do tom do cartão; o que é
  "papel" (balão, mostrador, folha) sai da `superficie`, e por isso escurece
  junto no modo escuro.
*/

function Bolhas() {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden="true">
      {/* O balão de quem escreveu, com as linhas do texto. */}
      <path
        d="M12 16h26a6 6 0 0 1 6 6v10a6 6 0 0 1-6 6H22l-7 6v-6h-3a6 6 0 0 1-6-6V22a6 6 0 0 1 6-6Z"
        className="fill-superficie"
      />
      <g fill="currentColor">
        <rect x="13" y="23" width="22" height="3" rx="1.5" opacity="0.45" />
        <rect x="13" y="29" width="14" height="3" rx="1.5" opacity="0.3" />
        {/* A resposta que ainda falta: o balão da casa, vazio, só com os três pontinhos. */}
        <path d="M34 36h16a6 6 0 0 1 6 6v4a6 6 0 0 1-6 6h-1v5l-6-5h-9a6 6 0 0 1-6-6v-4a6 6 0 0 1 6-6Z" opacity="0.9" />
      </g>
      <g className="fill-superficie">
        <circle cx="36" cy="44" r="1.8" />
        <circle cx="42" cy="44" r="1.8" />
        <circle cx="48" cy="44" r="1.8" />
      </g>
    </svg>
  )
}

function Fila() {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden="true">
      {/* Três conversas uma atrás da outra, a da frente inteira. */}
      <rect x="18" y="12" width="34" height="14" rx="5" fill="currentColor" opacity="0.2" />
      <rect x="14" y="20" width="38" height="16" rx="5" fill="currentColor" opacity="0.38" />
      <rect x="10" y="30" width="42" height="20" rx="6" className="fill-superficie" />
      <g fill="currentColor">
        <circle cx="19" cy="40" r="4" opacity="0.5" />
        <rect x="27" y="36" width="18" height="3" rx="1.5" opacity="0.45" />
        <rect x="27" y="42" width="12" height="3" rx="1.5" opacity="0.3" />
      </g>
      {/* O lugar do responsável, tracejado porque ninguém pegou. */}
      <circle
        cx="50"
        cy="50"
        r="7"
        className="fill-superficie"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeDasharray="3 2.4"
      />
    </svg>
  )
}

function Relogio() {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden="true">
      <circle cx="30" cy="32" r="19" className="fill-superficie" />
      <g stroke="currentColor" fill="none" strokeLinecap="round">
        <circle cx="30" cy="32" r="19" strokeWidth="2" opacity="0.45" />
        {/* Marcas das horas: só as quatro principais, para não virar textura. */}
        <path d="M30 16v3M30 45v3M46 32h-3M17 32h-3" strokeWidth="2" opacity="0.4" />
        <path d="M30 32v-9M30 32l6 4" strokeWidth="2.6" />
      </g>
      <circle cx="30" cy="32" r="2" fill="currentColor" />
      {/* O avião de papel: a mensagem que sai sozinha no horário. */}
      <path d="M44 44l14-6-5 14-3-5-6-3Z" fill="currentColor" opacity="0.85" />
    </svg>
  )
}

function Lista() {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden="true">
      <rect x="14" y="10" width="36" height="46" rx="6" className="fill-superficie" />
      {/* O prendedor da prancheta. */}
      <rect x="24" y="7" width="16" height="7" rx="3" fill="currentColor" opacity="0.6" />
      <g fill="currentColor">
        <rect x="20" y="22" width="7" height="7" rx="2" />
        <rect x="31" y="24" width="13" height="3" rx="1.5" opacity="0.45" />
        <rect x="20" y="34" width="7" height="7" rx="2" opacity="0.3" />
        <rect x="31" y="36" width="10" height="3" rx="1.5" opacity="0.3" />
        <rect x="20" y="46" width="7" height="7" rx="2" opacity="0.3" />
        <rect x="31" y="48" width="12" height="3" rx="1.5" opacity="0.3" />
      </g>
      {/* A primeira já conferida. */}
      <path
        d="M21.6 25.6l1.7 1.7 3-3.3"
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="stroke-superficie"
      />
    </svg>
  )
}
